/**
 * Lazy Image Loading Class
 *
 * This class manages the lazy loading of images on the webpage. It delays the loading
 * of images until they are close to the viewport, improving page performance and
 * loading speed.
 *
 * Properties:
 * - options: Configuration options including the threshold for lazy loading and
 *   a collection of images to load.
 *
 * Functions:
 * - init(): Initializes the lazy loading by setting up event listeners and
 *   triggering the initial check for images in the viewport.
 * - inview(): Checks which images are in the viewport and triggers the lazy load
 *   event for those images.
 * - update(): Manually triggers the inview check to load images.
 *
 * Example:
 * document.addEventListener('DOMContentLoaded', () => new LazyImages().init());
 */

class LazyImages {
    constructor() {
        this.options = {
            th: 300,
            images: document.querySelectorAll('img[data-src]'),
            loaded: null
        };
    }

    init() {
        const retina = window.devicePixelRatio > 1;
        const attr = retina ? 'data-src-retina' : 'data-src';

        this.options.images.forEach(img => {
            img.addEventListener('lazy', () => {
                const source = img.getAttribute(attr) || img.getAttribute('data-src');
                if (source) {
                    img.setAttribute('src', source);
                    img.removeAttribute('data-src');
                }
            });
        });

        ['scroll', 'resize', 'lookup'].forEach(event =>
            window.addEventListener(event, this.inview.bind(this))
        );

        this.inview();
    }

    inview() {
        const windowHeight = window.innerHeight;
        const scrollTop = window.scrollY;

        const inview = Array.from(this.options.images).filter(img => {
            if (img.offsetParent === null) return false;

            const imgRect = img.getBoundingClientRect();
            const imgTop = imgRect.top + scrollTop;
            const imgBottom = imgTop + imgRect.height;
            const windowBottom = scrollTop + windowHeight;

            return (
                imgBottom >= scrollTop - this.options.th && imgTop <= windowBottom + this.options.th
            );
        });

        inview.forEach(img => img.dispatchEvent(new Event('lazy')));
        this.options.loaded = inview;
      this.options.images = Array.from(this.options.images).filter(img => !this.options.loaded.includes(img));
    }

    update() {
        this.inview();
    }
}

document.addEventListener('DOMContentLoaded', () => new LazyImages().init());
